// Templates (README scope 6): the WhatsApp templates approved in the provider's console, listed here with their ID so
// broadcasts and chats outside the 24-hour window can use them. Admins add / edit; everyone on the team reads.
import { rpc, sb } from "../sb.ts";
import { t } from "../i18n.ts";
import { varCount } from "../../supabase/functions/_shared/broadcast.ts";
import { h, mount, toast } from "../ui.ts";

export interface Tpl { id: number; provider_ref: string | null; name: string; lang: string; category: string; body: string;
  status: string; reason: string | null; updated_at: string }

export async function templates(view: HTMLElement, role: string) {
  const isAdmin = role === "admin";
  const { data: st } = await sb.from("settings").select("provider").maybeSingle();
  const byName = st?.provider === "360dialog" || st?.provider === "meta";   // these send a template by its name
  const box = h("section", { class: "page" });
  const edit = h("div", {});
  const listEl = h("div", {});
  mount(view, box);
  mount(box, h("h2", {}, t("Templates")),
    h("p", { class: "muted" }, byName ? t("Create templates in WhatsApp Manager (360dialog Hub → your number). When WhatsApp approves one, add it here with the same name and language code.")
      : t("Create and submit templates in your provider's console (Twilio: Content Template Builder). When WhatsApp approves one, add it here with its Content SID (HX…).")),
    isAdmin && h("div", { class: "row end" }, h("button", { class: "primary", onclick: () => form(null) }, "+ " + t("New template"))),
    edit, listEl);

  async function load() {
    const { data, error } = await sb.from("wa_templates").select("*").order("status").order("name");
    if (error) return mount(listEl, h("p", { class: "err" }, error.message));
    const rows = data as Tpl[];
    mount(listEl, rows.length ? h("table", {}, h("tbody", {}, rows.map((x) => h("tr", {},
      h("td", {}, h("b", {}, x.name), h("div", { class: "muted", dir: "ltr" }, x.provider_ref ?? "—")),
      h("td", {}, h("p", { class: "pre", dir: "auto" }, x.body)),
      h("td", {}, h("span", { class: "pill" + (x.status === "approved" ? " ok" : x.status === "rejected" ? " bad" : "") }, t(x.status)),
        h("div", { class: "muted" }, t(x.category), " · ", x.lang), x.reason && h("small", { class: "muted" }, x.reason)),
      h("td", {}, isAdmin && h("button", { class: "link", onclick: () => form(x) }, t("Edit"))))))) : h("p", { class: "muted" }, t("No templates yet")));
  }

  function form(x: Tpl | null) {
    const name = h("input", { value: x?.name ?? "", placeholder: t("e.g. autumn_offer") });
    const ref = h("input", { value: x?.provider_ref ?? "", placeholder: byName ? t("same as the name") : "HX…", dir: "ltr", hidden: byName });
    const langs = [["en", "English (en)"], ["en_US", "English US (en_US)"], ["en_GB", "English UK (en_GB)"], ["ar", "العربية (ar)"]];
    if (x?.lang && !langs.some(([v]) => v === x.lang)) langs.push([x.lang, x.lang]);
    const lang = h("select", {}, langs.map(([v, l]) => h("option", { value: v, selected: (x?.lang ?? "en") === v }, l)));
    const cat = h("select", {}, ["marketing", "utility", "authentication"].map((v) => h("option", { value: v, selected: (x?.category ?? "marketing") === v }, t(v))));
    const status = h("select", {}, ["approved", "pending", "rejected", "not_submitted"].map((v) => h("option", { value: v, selected: (x?.status ?? "approved") === v }, t(v))));
    const body = h("textarea", { rows: 4, dir: "auto", placeholder: t("Exactly as approved, e.g. Hello {{1}}, our offer: {{2}}") }, x?.body ?? "");
    const vars = h("small", { class: "muted" });
    const showVars = () => { const n = varCount(body.value); vars.textContent = n ? t("{n} variables: filled per message", { n }) : t("No variables"); };
    body.addEventListener("input", showVars);
    showVars();
    const reason = h("input", { value: x?.reason ?? "", placeholder: t("optional: why rejected, notes") });
    mount(edit, h("div", { class: "card-in" },
      h("h3", {}, x ? t("Edit template") : t("New template")),
      h("div", { class: "row" }, h("label", {}, byName ? t("Template name in WhatsApp Manager") : t("Name"), name), !byName && h("label", {}, t("Content SID"), ref)),
      h("div", { class: "row" }, h("label", {}, t("Language"), lang), h("label", {}, t("Category"), cat), h("label", {}, t("Status"), status)),
      h("label", {}, t("Text"), body), vars,
      h("label", {}, t("Notes"), reason),
      h("div", { class: "actions" },
        x && h("button", { onclick: async () => {
          if (!confirm(t("Delete this template?"))) return;
          try { await rpc("delete_template", { p_id: x.id }); mount(edit); load(); } catch (e) { toast((e as Error).message, true); }
        } }, t("Delete")),
        h("button", { onclick: () => mount(edit) }, t("Cancel")),
        h("button", { class: "primary", onclick: async () => {
          try {
            await rpc("save_template", { p: { id: x?.id ?? null, name: name.value, provider_ref: ref.value, lang: lang.value, category: cat.value,
              status: status.value, body: body.value, reason: reason.value } });
            toast(t("Saved")); mount(edit); load();
          } catch (e) { toast((e as Error).message, true); }
        } }, t("Save")))));
    name.focus();
  }

  await load();
}
